<template>
  <div>
    <div v-show="isActive">
      <validation-observer ref="observer" v-slot="{ handleSubmit }">
        <div class="columns toolbar" style="position:relative;">
          <div class="column is-narrow filters-column">
            <div class="filters">
              <div class="filter">
                <label>User:</label>
                <b-field>
                  <b-select class="control is-fullwidth template-selector"
                            :disabled="currentTemplateEditing"
                            v-model="userTypeId">
                    <option v-for="customerType in customerTypes"
                            :value="customerType.id"
                            :key="customerType.id">Customer: {{ customerType.value }}
                    </option>
                  </b-select>
                </b-field>
              </div>
              <div class="filter">
                <label>Template:</label>
                <b-field class="template-selector-wrapper">
                  <b-select class="control is-fullwidth template-selector"
                            @change.native="setLastUsedTitle($event.target.value)"
                            :disabled="currentTemplateEditing"
                            v-model="selectedTemplate.id">
                    <option v-for="template in flyerTemplates"
                            :value="template.id"
                            :key="template.id">{{ template.title }}
                    </option>
                  </b-select>
                </b-field>
              </div>
            </div>
          </div>
          <div class="column flyer-btns"
               :class="{'buttons-alignment':currentTemplateEditing}">
            <div class="buttons">
              <template v-if="!currentTemplateEditing">
                <b-button
                  @click="openFlyerInNewTab"
                  icon-pack="fas"
                  class="modern-button is-outlined"
                >
                  open
                </b-button>
                <template v-if="hasAccessToModifyMarketingMaterials">
                  <b-button
                    class="modern-button"
                    @click="editSelectedTemplate"
                  >
                    edit
                  </b-button>
                </template>
              </template>
              <template v-else>
                <b-button
                  @click="setSectionNotEditable"
                  class="modern-button is-outlined"
                >
                  cancel
                </b-button>

                <b-button
                  class="modern-button"
                  @click="handleSubmit(applyChangesSelectedTemplate)"
                >
                  submit
                </b-button>
              </template>
            </div>
          </div>
        </div>
        <div class="columns">
          <div class="column is-7">
            <template v-if="!currentTemplateEditing">
              <b-field>
                <span>{{ selectedTemplate.description }}</span>
              </b-field>
              <div class="flyer-examples"
                   v-if="previewImageFrontSide || previewImageBackSide">
                <img class="" :src="previewImageFrontSide"
                     alt="Flyer 1 example">
                <img class="" :src="previewImageBackSide"
                     alt="Flyer 2 example">
              </div>
              <b-field>
              <span class="settings-text--gray">
                Template last updated: {{ lastUsedTemplateTitle }}
              </span>
              </b-field>
            </template>
            <template v-else>
              <form @submit.prevent="handleSubmit(applyChangesSelectedTemplate)">
                <div class="block">
                  <b-field>
                    <span>Description</span>
                  </b-field>
                  <textarea class="textarea"
                            v-model="selectedTemplate.description"
                            @blur="() => { trimSpaces('description') }"
                            rows="5">
                </textarea>
                </div>
                <div class="block">
                  <b-field>
                    <span>HTML-Code</span>
                  </b-field>
                  <validation-provider v-slot="{ errors }" rules="required">
                    <b-field :type="{ 'is-danger': errors.length }"
                             v-model="selectedTemplate.body"
                             :message="errors + [ '&nbsp;' ]">
                      <b-input type="textarea" v-model="selectedTemplate.body" rows="15"></b-input>
                    </b-field>
                  </validation-provider>
                </div>

                <b-button
                  class="modern-button is-outlined"
                  @click="handleSubmit(getFlyerPreviewFile)"
                >
                  preview
                </b-button>
              </form>
            </template>
          </div>
        </div>
      </validation-observer>
    </div>

    <div class="columns" style="position:relative;">
      <template v-if="activeSnackbar" ref="snackbarOpenFlyer">
        <div class="openFlyerSnackbar notices is-top">
          <div class="openFlyer snackbar is-success is-top" role="alertdialog">
            <div class="text">
              Click to open the flyer in a new tab
            </div>
            <div class="action is-success">
              <button type="button" class="button" @click="openFlyerInNewTab">
                Open flyer
              </button>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>

import { mapGetters, mapMutations, mapState } from 'vuex';
import * as MUTATIONS from '@/store/mutations';
import {
  getFlyerTemplate,
  getFlyerTemplates,
  updateFlyerTemplate,
} from '@/api/flyer_templates';
import {
  getFlyerTemplateSetting,
  updateFlyerTemplateSetting,
} from '@/api/settings';
import _ from 'lodash';
import {
  MARKETING_MATERIALS_MANAGE_MODIFY,
} from '@/const/privileges';
import { CUSTOMER_TYPE_CONTRACTOR_ID } from '@/const/constants';

export default {
  name: 'Flyer',
  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeSnackbar: false,
      userTypeId: CUSTOMER_TYPE_CONTRACTOR_ID,
      selectedTemplate: '',
      lastUsedTemplateTitle: '',
      currentTemplateEditing: false,
      flyerTemplates: [],
      previewImageBackSide: null,
      previewImageFrontSide: null,
    };
  },

  computed: {
    ...mapGetters(['authAuthenticator', 'hasAccess']),
    ...mapState('constant', ['customerTypes']),
    hasAccessToModifyMarketingMaterials() {
      return this.hasAccess(
        MARKETING_MATERIALS_MANAGE_MODIFY,
        MARKETING_MATERIALS_MANAGE_MODIFY,
        true,
      );
    },
  },
  watch: {
    userTypeId() {
      this.selectedTemplateId = null;
      this.getUpdatedData();
    },
  },
  activated() {
    this.getUpdatedData();
  },
  mounted() {
    window.addEventListener('click', this.closeSnackbar);
  },
  methods: {
    checkAccess(privilege) {
      return this.hasAccess(privilege, privilege, true);
    },
    ...mapMutations({
      startLoading: MUTATIONS.START_LOADING,
      finishLoading: MUTATIONS.FINISH_LOADING,
    }),
    setLastUsedTitle(val) {
      this.selectedTemplateId = val;
      this.getUpdatedData();
    },
    getFlyerPreviewFile() {
      this.startLoading();
      const payload = new FormData();
      payload.set('id', this.selectedTemplate.id);
      payload.set('body', this.selectedTemplate.body);
      getFlyerTemplate(payload).then((response) => {
        if (response.headers['content-type'] === 'application/json') {
          const fReader = new FileReader();
          fReader.readAsText(response.data);
          fReader.addEventListener('loadend', () => {
            const data = JSON.parse(fReader.result);
            if (data.error) {
              this.toast(data.message, 'is-danger');
            }
          });
        } else {
          const file = window.URL.createObjectURL(new Blob([response.data], {
            type: 'application/pdf',
          }));
          window.open(file);
        }
      })
        .finally(() => {
          this.finishLoading();
        });
    },
    trimSpaces(field) {
      this.selectedTemplate[field] = this.selectedTemplate[field].trim().replace(/\s+/gu, ' ');
    },
    applyChangesSelectedTemplate() {
      this.startLoading();
      const payloadForSetting = new FormData();
      payloadForSetting.set('id', this.flyerTemplateSetting.paramId);
      payloadForSetting.set('paramName', 'flyerTemplate');
      payloadForSetting.set('templateId', this.selectedTemplate.id);
      const payloadForFlyerTemplate = new FormData();
      payloadForFlyerTemplate.set('id', this.selectedTemplate.id);
      if (this.selectedTemplate.description) {
        payloadForFlyerTemplate.set('description', this.selectedTemplate.description);
      }
      payloadForFlyerTemplate.set('body', this.selectedTemplate.body);
      updateFlyerTemplateSetting(payloadForSetting).then((response) => {
        if (response.data.error) {
          this.toast(response.data.message, 'is-danger');
        } else {
          this.updateFlyerTemplate(payloadForFlyerTemplate);
        }
      })
        .finally(() => {
          this.finishLoading();
        });
    },
    setSectionNotEditable() {
      this.currentTemplateEditing = false;
      if (this.flyerTemplates.length) {
        const elem = this.flyerTemplates.find((item) => item.id === this.selectedTemplate.id);
        this.selectedTemplate = _.clone(elem);
      }
    },
    openFlyerInNewTab() {
      this.startLoading();
      const payload = new FormData();
      payload.set('id', this.selectedTemplate.id);
      getFlyerTemplate(payload).then((response) => {
        if (response.headers['content-type'] === 'application/json') {
          const fReader = new FileReader();
          fReader.readAsText(response.data);
          fReader.addEventListener('loadend', () => {
            const data = JSON.parse(fReader.result);
            if (data.error) {
              this.toast(data.message, 'is-danger');
            }
          });
        } else {
          const file = window.URL.createObjectURL(new Blob([response.data], {
            type: 'application/pdf',
          }));
          window.open(file);
        }
      })
        .finally(() => {
          this.finishLoading();
        });
    },
    async getUpdatedData() {
      this.startLoading();
      try {
        await this.handlingGetFlyerTemplates();
        await this.handlingGetFlyerTemplateSetting();
      } catch (error) {
        this.$buefy.snackbar.open({
          message: 'An unexpected error occurred, please contact support@enhancify.com',
          actionText: ' ',
          position: 'is-top',
          indefinite: true,
          type: 'is-danger',
        });
      }
      this.finishLoading();
    },
    async handlingGetFlyerTemplateSetting() {
      await getFlyerTemplateSetting().then((resp) => {
        let params = '';
        this.lastUsedEmail = '';
        this.lastUsedDates = null;
        if (resp.data.total === 1) {
          this.flyerTemplateSetting = { ...resp.data.data[0] };
          if (this.flyerTemplateSetting.paramVal) {
            params = JSON.parse(this.flyerTemplateSetting.paramVal);
          }
          if (this.flyerTemplateSetting.userId) {
            this.lastUsedEmail = this.flyerTemplateSetting.userName;
            if (params) {
              this.contractorId = params.contractorId;
              this.lastUsedDates = params.lastUpdated;
            }
          }
        }
        if (this.lastUsedDates) {
          if (Object.prototype.hasOwnProperty.call(this.lastUsedDates, 'flyerTemplate')) {
            const lastUpdateInfo = this.lastUsedDates.flyerTemplate;
            if (lastUpdateInfo[this.selectedTemplateId]) {
              this.lastUsedTemplateTitle = `${lastUpdateInfo[this.selectedTemplateId].userName}, `;
              this.lastUsedTemplateTitle += `${this.dateToString(new Date(lastUpdateInfo[this.selectedTemplateId].date))}`;
            } else {
              this.lastUsedTemplateTitle = '';
            }
          }
        }
      });
    },
    async handlingGetFlyerTemplates() {
      await getFlyerTemplates(this.userTypeId).then((response) => {
        this.flyerTemplates = response.data;
        if (!this.selectedTemplateId) {
          this.selectedTemplate = { ...response.data[0] };
          this.selectedTemplateId = this.selectedTemplate.id;
        } else {
          const elem = this.flyerTemplates.find(
            (item) => item.id === Number(this.selectedTemplateId),
          );
          this.selectedTemplate = elem ? _.clone(elem) : { ...response.data[0] };
          this.selectedTemplateId = this.selectedTemplate.id;
        }
        if (this.selectedTemplate.previewImageFrontSide
            || this.selectedTemplate.previewImageBackSide) {
          this.previewImageFrontSide = this.selectedTemplate.previewImageFrontSide;
          this.previewImageBackSide = this.selectedTemplate.previewImageBackSide;
        } else {
          this.previewImageFrontSide = '';
          this.previewImageBackSide = '';
        }
      });
    },
    editSelectedTemplate() {
      this.currentTemplateEditing = true;
    },
    dateToString(date) {
      return date.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    },
    updateFlyerTemplate(payloadForFlyerTemplate) {
      this.startLoading();
      updateFlyerTemplate(payloadForFlyerTemplate).then((response) => {
        if (response.data.error) {
          this.toast(response.data.message, 'is-danger');
        } else {
          this.setSectionNotEditable();
          this.getUpdatedData();
          this.toast(response.data.message);
        }
      }).finally(() => {
        this.finishLoading();
      });
    },
    toast(message, type = 'is-success') {
      this.$buefy.toast.open({ message, type, duration: 5000 });
    },
    closeSnackbar(event) {
      if (event.composedPath().includes(this.$refs.snackbarOpenFlyer)) return;
      this.activeSnackbar = false;
    },
  },
};
</script>

<style lang="stylus" scoped>
.toolbar
  flex-wrap wrap
  row-gap 15px
.filters-column
  max-width 100%
  min-width 0
.filters
  display flex
  align-items center
  column-gap 30px
  flex-wrap wrap
  row-gap 15px
  max-width 100%
.filter
  display flex
  align-items center
  min-width 0
  max-width 100%
  & label
    font-weight 600
    margin-right 10px
    white-space nowrap
  & .field
    margin-bottom 0
    min-width 0
.template-selector-wrapper
  width: 445px;
  max-width: 100%;
.template-selector-wrapper .control.template-selector
  width: 100%;
.template-selector-wrapper >>> span.select
  width: 100%;
.template-selector-wrapper >>> select
  width: 100%;
button.button
  min-width: 110px;
  text-transform uppercase

.flyer-btns
  display flex
  align-items end
.buttons-alignment
  justify-content right

.control.template-selector
  max-width 100%
.control.template-selector >>> span.select
  height 48px
  max-width 100%
.control.template-selector >>> select
  height 48px
  max-width 100%

.flyer-examples
  margin-bottom 20px
  display: flex
  flex-direction: row
  align-items center
  @media(max-width:499px)
    flex-wrap wrap
  @media(min-width:500px)
    flex-direction: row
    justify-content: space-between
  img
    box-shadow: 0 0 20px rgba(156 173 173 .3)
    margin-top: 20px
    width: 100%
    object-fit contain
    @media(min-width:500px)
      width: calc(50% - 10px)

.tabs
  display flex
  justify-content start
  border-bottom 1px solid rgba(143, 167, 179, 0.3)
  overflow visible
  .tab
    cursor pointer
    margin 0 15px
    padding-bottom: 18px
    &:first-child
      margin: 0 15px 0 0
    &.is-active
      position relative
      font-weight 600
      &:after
        content: ''
        position absolute
        bottom -1.5px
        left 0
        width 100%
        height 3px
        background rgba(23, 181, 72, 1)
</style>
